<template>
    <div class="student-theme back-section flex flex-wrap align-self-start pt-2 justify-content-between w-full">
        <Button icon="pi pi-arrow-left" label="Back" class="p-button-text Caption back-button mb-2"
            @click="$emit('back')" />

        <div v-if="loading" class="flex justify-content-center align-items-center w-full h-30rem">
            <ProgressSpinner style="width: 50px; height: 50px" />
        </div>

        <div v-else class="flex flex-column justify-content-center align-items-center w-full h-full">
            <span class="H6-Medium py-4">Reschedule Class</span>

            <form @submit.prevent="handleSave" class="form w-full">
                <div class="grid w-full align-items-start justify-content-center">

                    <!-- Left Column: Read-only fields -->
                    <div class="col-12 md:col-6">
                        <!-- <FieldWrapper label="Student">
                            <InputText :value="classDetails?.username" class="w-full" variant="filled" size="small"
                                disabled />
                        </FieldWrapper> -->

                        <FieldWrapper label="Subject">
                            <InputText :value="cleanupLables(classDetails?.subject)" class="w-full" variant="filled"
                                size="small" disabled />
                        </FieldWrapper>

                        <FieldWrapper label="grade">
                            <InputText :value="cleanupLables(classDetails?.grade)" class="w-full" variant="filled"
                                size="small" disabled />
                        </FieldWrapper>

                        <FieldWrapper label="language">
                            <InputText :value="cleanupLables(classDetails?.language)" class="w-full" variant="filled"
                                size="small" disabled />
                        </FieldWrapper>

                        <FieldWrapper label="Class Model">
                            <InputText :value="cleanupLables(classDetails?.classModel)" class="w-full" variant="filled"
                                size="small" disabled />
                        </FieldWrapper>

                        <FieldWrapper label="Class Type">
                            <InputText :value="cleanupLables(classDetails?.classType)" class="w-full" variant="filled"
                                size="small" disabled />
                        </FieldWrapper>

                        <FieldWrapper v-if="classDetails?.locationAddress" label="Location Address">
                            <InputText :value="classDetails.locationAddress" class="w-full" variant="filled"
                                size="small" disabled />
                        </FieldWrapper>
                    </div>

                    <!-- Right Column: Editable fields -->
                    <div class="col-12 md:col-6 flex flex-column">

                        <!-- New Date -->
                        <FieldWrapper label="New Date" :required="true">
                            <DatePicker v-model="form.date" appendTo="self" dateFormat="dd/mm/yy" showIcon iconDisplay="input"
                                class="w-full" variant="filled" size="small" :invalid="!!errors.date" />
                            <small v-if="errors.date" class="error-text-block">{{ errors.date }}</small>
                        </FieldWrapper>

                        <!-- New Time Slot -->
                        <FieldWrapper label="New Time Slot" :required="true">
                            <div class="flex flex-column sm:flex-row gap-2 w-full">
                                <DatePicker v-model="form.startTime" timeOnly hourFormat="12" showIcon
                                    iconDisplay="input" variant="filled" placeholder="Start" size="small" class="w-full"
                                    :invalid="!!errors.startTime" @date-select="validateTimeSlot" />
                                <DatePicker v-model="form.endTime" :minDate="form.startTime" timeOnly hourFormat="12" showIcon iconDisplay="input"
                                    variant="filled" placeholder="End" size="small" class="w-full"
                                    :invalid="!!errors.endTime" @date-select="validateTimeSlot"/>
                            </div>
                            <small v-if="errors.startTime || errors.endTime" class="error-text-block">
                                {{ errors.startTime ? 'Start time is required' : errors.endTime }}
                            </small>
                        </FieldWrapper>

                        <!-- Reschedule Scope -->
                        <div class="flex flex-column gap-3 pt-4">
                            <label class="Label align-self-start">Reschedule Scope</label>
                            <div class="flex flex-column sm:flex-row gap-2 w-full">
                                <div class="flex align-items-center gap-3 w-full">
                                    <RadioButton v-model="form.scope" inputId="scopeSingle" name="scope" value="single"
                                        class="orange-radio-button" />
                                    <label for="scopeSingle" class="Paragraph-Regular">Only this class</label>
                                </div>
                                <div v-if="classDetails?.recurrence?.enabled"
                                    class="flex align-items-center gap-3 w-full">
                                    <RadioButton v-model="form.scope" inputId="scopeAll" name="scope" value="all"
                                        class="orange-radio-button" />
                                    <label for="scopeAll" class="Paragraph-Regular">All upcoming classes</label>
                                </div>
                            </div>
                        </div>

                        <div v-if="form.scope === 'all'" class="mt-1 w-full">
                            <Divider align="left">
                                <span class="Caption text-500">Recurring Settings</span>
                            </Divider>

                            <div class="flex flex-column gap-3">

                                <div class="flex flex-wrap align-items-center gap-2">
                                    <span class="placeholder-text">Repeat every</span>
                                    <InputText :value="String(classDetails?.recurrence?.repeatWeeks ?? '')"
                                        variant="filled" size="small" class="text-center w-4rem" disabled />
                                    <span class="placeholder-text pl-2">Week(s)</span>
                                </div>

                                <div class="flex align-items-center gap-4 w-full">
                                    <span class="whitespace-nowrap placeholder-text pt-1">Repeat on</span>
                                    <div class="flex gap-2 flex-wrap overflow-auto p-1">
                                        <Button :label="day.label" variant="text" raised v-for="day in days"
                                            :key="day.code" @click="toggleDay(day.code)"
                                            class="day-selector-button border-1 duration-150"
                                            :class="{ 'active': form.repeatDays.includes(day.code) }" />
                                    </div>
                                </div>

                                <div class="flex flex-wrap align-items-center gap-2">
                                    <span class="placeholder-text pr-4">Ends after</span>
                                    <InputText :value="String(classDetails?.recurrence?.totalOccurrences ?? '')"
                                        variant="filled" size="small" class="text-center w-4rem" disabled />
                                    <span class="placeholder-text pl-2">Occurrence(s)</span>
                                </div>

                            </div>
                        </div>
                    </div>

                    <!-- Submit -->
                    <div class="col-12 flex justify-content-end">
                        <Button label="Send Reschedule Request" class="orange-button mt-4" type="submit" :loading="saving" />
                    </div>

                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, defineProps, defineEmits } from 'vue';
import { fetchClassInstanceDetails, classRescheduleRequest } from '@/api/student';
import Button from "primevue/button";
import Divider from "primevue/divider";
import InputText from "primevue/inputtext";
import DatePicker from 'primevue/datepicker';
import RadioButton from 'primevue/radiobutton';
import ProgressSpinner from 'primevue/progressspinner';
import FieldWrapper from '@/components/FieldWrapper.vue';
import { notifyError, notifySuccess } from '@/utils/notification';
import { cleanupLables, toLocalISOString } from "@/utils/cleanups";

const props = defineProps({
    classInstanceId: { type: String, required: true },
});

const emit = defineEmits(['back', 'success']);

const loading = ref(true);
const saving = ref(false);
const classDetails = ref(null);
const errors = ref({});

const days = [
    { label: 'M', code: 'monday' }, { label: 'Tu', code: 'tuesday' },
    { label: 'W', code: 'wednesday' }, { label: 'Th', code: 'thursday' },
    { label: 'F', code: 'friday' }, { label: 'Sa', code: 'saturday' },
    { label: 'S', code: 'sunday' },
];

const form = reactive({
    date: null,
    startTime: null,
    endTime: null,
    scope: 'single',
    repeatDays: []
});

onMounted(async () => {
    try {
        const response = await fetchClassInstanceDetails(props.classInstanceId);
        const data = response.data.data;
        classDetails.value = data;

        form.date = new Date(data.date);
        form.startTime = parseTimeString(data.start);
        form.endTime = parseTimeString(data.end);

        if (data.recurrence?.repeatOn) {
            form.repeatDays = [...data.recurrence.repeatOn];
        }
    } catch (error) {
        notifyError('Error', 'Failed to fetch class details.');
    } finally {
        loading.value = false;
    }
});

const parseTimeString = (timeStr) => {
    if (!timeStr) return null;

    const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (timeMatch) {
        let [, h, m, p] = timeMatch;
        h = parseInt(h);
        m = parseInt(m);
        if (p.toUpperCase() === 'PM' && h < 12) h += 12;
        if (p.toUpperCase() === 'AM' && h === 12) h = 0;
        const d = new Date();
        d.setHours(h, m, 0, 0);
        return d;
    }

    const [hours, minutes] = timeStr.split(':');
    const d = new Date();
    d.setHours(parseInt(hours), parseInt(minutes), 0);
    return d;
};

const toggleDay = (dayCode) => {
    const index = form.repeatDays.indexOf(dayCode);
    if (index > -1) form.repeatDays.splice(index, 1);
    else form.repeatDays.push(dayCode);
};

const combineDateAndTime = (dateSource, timeSource) => {
        if (!dateSource || !timeSource) return null;
        const d = new Date(dateSource);
        const t = new Date(timeSource);
        t.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
        return toLocalISOString(t);
};

const validateTimeSlot = () => {
    if (form.startTime && form.endTime) {
        if (form.endTime.getTime() <= form.startTime.getTime()) {
            errors.value.endTime = "End time must be after start time";
            form.endTime = null;
            return false;
        }
    }
    errors.value.endTime = null;
    return true;
};

const handleSave = async () => {
    errors.value = {};
    if (!form.date) errors.value.date = "Date is required";
    if (!form.startTime) errors.value.startTime = "Start time is required";
    if (!form.endTime) {
        errors.value.endTime = "Valid end time is required";
    }

    if (Object.keys(errors.value).length > 0) return;

    saving.value = true;
    try {
        const payload = {
            instanceId: props.classInstanceId,
            type: form.scope,                 // Changed from scope ('single' or 'all')
            date: toLocalISOString(form.date),
            start: combineDateAndTime(form.date, form.startTime),
            end: combineDateAndTime(form.date, form.endTime),
            repeatOn: form.scope === 'all' ? form.repeatDays : undefined
        };

        await classRescheduleRequest(payload);
        notifySuccess('Success', 'Class reschedule request sent successfully');
        emit('success');
    } catch (error) {
        if (error.response && error.response.status === 409) {
            notifyError("Conflict", error.response?.data?.message);
        } else {
            notifyError("Error", error.response?.data?.message || "Failed to send reschedule request. Please try again.");
        }
    } finally {
        saving.value = false;
    }
};

watch(() => form.date, () => errors.value.date = null);
watch(() => form.startTime, () => errors.value.startTime = null);
</script>

<style scoped>
:deep(.p-inputnumber-button-group) button {
    padding: 0.25rem 0.5rem !important;
}

/* Ensure DatePicker input stretches full width */
:deep(.p-datepicker) {
    width: 100%;
}

:deep(.p-datepicker .p-inputtext) {
    width: 100%;
}

.day-selector-button {
    font-size: 0.85rem;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    padding: 12px !important;
    background-color: #ffffff;
    border-color: var(--gray-600);
    color: var(--gray-600);
}

.day-selector-button:not(.active):hover {
    background-color: var(--gray-100) !important;
    color: var(--gray-600);
}

.day-selector-button.active {
    background-color: var(--orange-500);
    border-color: var(--orange-500);
    color: #ffffff !important;
}

.day-selector-button.active:hover {
    background-color: var(--orange-600) !important;
    border-color: var(--orange-600) !important;
}

/* Inline error below field (no absolute positioning) */
.error-text-block {
    color: #dc2626;
    font-size: 0.85rem;
    display: block;
    margin-top: 0.25rem;
}
</style>